import { useState, type FormEvent } from "react";
import { TotpMultiFactorGenerator, type MultiFactorResolver } from "firebase/auth";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

/**
 * Second step of a sign-in that requires MFA. Firebase throws
 * `auth/multi-factor-auth-required` on password sign-in for an enrolled user;
 * App builds a {@link MultiFactorResolver} from that error and hands it here to
 * collect + verify the TOTP code. On success, `resolveSignIn` completes the
 * sign-in and the session hook's `onAuthStateChanged` picks up the user.
 */
export function MfaChallenge({
  resolver,
  onCancel,
}: {
  resolver: MultiFactorResolver;
  onCancel: () => void;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const totpHint = resolver.hints.find(
    (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!totpHint) {
      setError("No authenticator app is enrolled for this account.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim());
      await resolver.resolveSignIn(assertion);
      // No navigation needed: onAuthStateChanged resolves the signed-in user and
      // unmounts this challenge.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper component="form" onSubmit={submit} sx={{ p: 4 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h1">Two-factor verification</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Enter the 6-digit code from your authenticator app.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Authentication code"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          fullWidth
        />

        <Stack spacing={1}>
          <Button type="submit" variant="contained" size="large" disabled={busy}>
            {busy ? "Verifying…" : "Verify"}
          </Button>
          <Button type="button" variant="text" onClick={onCancel} disabled={busy}>
            Back
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
